import { env } from "../core/env.js";
import { httpHeader } from "@bitcashier/shared/core/httpHeader.js";
import { getToken, reverifyMfa } from "../auth/auth.svelte.js";
import { readActiveClientId } from "../activeClientStorage.js";
import { clearViewingAs, viewingAsUserId } from "../viewAsStore.js";

// Replaces shared/api/action_utility.ts's transport half (axios + token + headers), on native
// fetch. The redux dispatch/thunk half is replaced by createFetchStore. `clientId: true` mirrors
// the old behaviour of stamping the active-client onto params (GET/DELETE) or body (POST/PUT).

const buildQuery = (params) => {
  const q = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") q.append(k, v);
  });
  const s = q.toString();
  return s ? `?${s}` : "";
};

// A client can require a fresh second factor before money moves or its routing changes (withdrawals, payouts, trades,
// withdrawal addresses, auto-convert rules, API keys and their IP allowlist, the callback secret). The backend refuses such a request with this code; the step-up prompt runs and the request is retried
// once with the new token. A second refusal is a real one (the factor was not completed) and reaches the caller.
export const MFA_REVERIFICATION_REQUIRED = "MfaReverificationRequired";

// What the backend answers once it stops accepting a view (the viewer is no longer a Super admin, or
// the user viewed is gone). Every request would fail the same way, so the view is dropped instead.
const REFUSED_VIEW =
  /view the panel as another user|Admin permission missing: view-as\.|act as a staff member|User .* not found/;

export async function request(method, uri, opts = {}) {
  try {
    return await send(method, uri, opts);
  } catch (err) {
    if (err?.data?.errorCode !== MFA_REVERIFICATION_REQUIRED) throw err;
    await reverifyMfa();
    return send(method, uri, opts);
  }
}

async function send(method, uri, { params, body, clientId } = {}) {
  const token = await getToken();
  const activeClient = readActiveClientId();
  const viewingAs = viewingAsUserId();
  const headers = viewingAs
    ? { ...httpHeader(token, activeClient), "X-Impersonate-User": viewingAs }
    : httpHeader(token, activeClient);

  let finalParams = { ...(params || {}) };
  let finalBody = body;
  if (clientId) {
    if (method === "GET" || method === "DELETE") finalParams.clientId = activeClient;
    else finalBody = { ...(body || {}), clientId: activeClient };
  }

  const init = { method, headers };
  if (finalBody !== undefined && method !== "GET" && method !== "DELETE") {
    init.headers = { ...headers, "Content-Type": "application/json" };
    init.body = JSON.stringify(finalBody);
  }

  const res = await fetch(`${env.apiUrl}${uri}${buildQuery(finalParams)}`, init);
  const text = await res.text();
  // Not every response is ours: a proxy 502/504, or Pekko rejecting an oversized upload with 413, answers in HTML.
  // Parsing that threw a SyntaxError which then *became* the message shown to the user ("Unexpected token '<'"),
  // burying the status that actually explains the failure.
  let data = null;
  let readable = true;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      readable = false;
    }
  }
  if (!res.ok) {
    // Start over as oneself rather than fail every screen.
    if (viewingAs && REFUSED_VIEW.test(data?.message ?? "")) {
      clearViewingAs();
      window.location.assign("/");
    }
    const err = new Error(data?.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  // A success we cannot read is still a failure — never quietly hand a caller a null body.
  if (!readable) throw new Error(`Unreadable response from the server (${res.status})`);
  return data;
}

export const api = {
  get: (uri, opts) => request("GET", uri, opts),
  post: (uri, body, opts) => request("POST", uri, { ...opts, body }),
  put: (uri, body, opts) => request("PUT", uri, { ...opts, body }),
  del: (uri, opts) => request("DELETE", uri, opts),
};
